import { zodResolver } from "@hookform/resolvers/zod";
import { useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router";
import { CircleCheck, MailCheck } from "lucide-react";
import { ClientResponseError } from "pocketbase";
import { toast } from "sonner";
import { z } from "zod";
import { ForgotPasswordDialog } from "@/components/auth/forgot-password-dialog";
import { LanguageSelect } from "@/components/controls/language-select";
import { OAuthButtons } from "@/components/auth/oauth-buttons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { LEGAL_URLS } from "@/lib/auth/legal";
import { isLocale, LOCALES } from "@/i18n";
import { useAuth } from "@/lib/auth/auth-context";
import type { ConfirmedState } from "@/lib/auth/confirm";
import { applyServerErrors } from "@/lib/core/form-errors";
import { prefetchPowSolution } from "@/lib/auth/pow";

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, login, register, resendVerification } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const tabParam = new URLSearchParams(location.search).get("tab");
  const [submitting, setSubmitting] = useState(false);
  /** Email of an account that exists but is not verified yet (login 403 or just registered). */
  const [unverified, setUnverified] = useState<{ email: string; justRegistered: boolean } | null>(
    null,
  );
  const redirectTo = (location.state as { from?: string } | null)?.from ?? "/dashboard";
  /** Set by the confirm pages the links in account emails open (routes/confirm-*.tsx). */
  const confirmed = (location.state as Partial<ConfirmedState> | null)?.confirmed;
  const confirmedEmail = (location.state as Partial<ConfirmedState> | null)?.email ?? "";

  // Schemas live inside the component so validation messages follow the current language.
  const loginSchema = z.object({
    email: z.email(t("validation.email")),
    password: z.string().min(8, t("validation.password")),
  });
  const registerSchema = loginSchema.extend({
    name: z.string().min(2, t("validation.name")),
    locale: z.enum(LOCALES),
    acceptTerms: z.boolean().refine(Boolean, t("validation.acceptTerms")),
  });
  type LoginValues = z.infer<typeof loginSchema>;
  type RegisterValues = z.infer<typeof registerSchema>;

  const loginForm = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: confirmedEmail, password: "" },
  });
  const registerForm = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      locale: isLocale(i18n.resolvedLanguage) ? i18n.resolvedLanguage : "en",
      acceptTerms: false,
    },
  });

  if (isAuthenticated) return <Navigate to={redirectTo} replace />;

  async function submitLogin(values: LoginValues) {
    setSubmitting(true);
    setUnverified(null);
    try {
      await login(values.email, values.password);
      toast.success(t("login.signedIn"));
      navigate(redirectTo, { replace: true });
    } catch (error) {
      // PocketBase answers 403 when the auth rule (verified = true) does not match.
      if (error instanceof ClientResponseError && error.status === 403) {
        setUnverified({ email: values.email, justRegistered: false });
        return;
      }
      const message = applyServerErrors(error, (name, err) =>
        loginForm.setError(name as keyof LoginValues, err),
      );
      if (message) toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function submitRegister(values: RegisterValues) {
    setSubmitting(true);
    try {
      await register(values);
      setUnverified({ email: values.email, justRegistered: true });
      registerForm.reset();
    } catch (error) {
      const message = applyServerErrors(
        error,
        (name, err) => registerForm.setError(name as keyof RegisterValues, err),
        ["name", "email", "password"],
      );
      if (message) toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function resend(email: string) {
    try {
      await resendVerification(email);
      toast.success(t("login.verificationSent"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
    }
  }

  const verifyNotice = unverified && (
    <Alert data-testid="verify-notice">
      <MailCheck />
      <AlertTitle>
        {unverified.justRegistered ? t("login.checkInbox") : t("login.notVerified")}
      </AlertTitle>
      <AlertDescription className="space-y-2">
        <p>{t("login.verifyHint", { email: unverified.email })}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void resend(unverified.email)}
          data-testid="resend-verification"
        >
          {t("login.resend")}
        </Button>
      </AlertDescription>
    </Alert>
  );

  return (
    <div className="mx-auto max-w-md">
      {/* /login?tab=register opens the registration (the website's "Start free" links). */}
      <Tabs defaultValue={tabParam === "register" ? "register" : "login"}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="login">{t("login.tabLogin")}</TabsTrigger>
          <TabsTrigger value="register" data-testid="tab-register">
            {t("login.tabRegister")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="login">
          <Card>
            <CardHeader>
              <CardTitle>{t("login.welcome")}</CardTitle>
              <CardDescription>{t("login.welcomeSubtitle")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {confirmed && !unverified && (
                <Alert data-testid="confirmed-notice">
                  <CircleCheck />
                  <AlertTitle>
                    {confirmed === "verified"
                      ? t("confirm.verified")
                      : t("confirm.passwordChanged")}
                  </AlertTitle>
                  <AlertDescription>
                    {confirmed === "verified"
                      ? t("confirm.verifiedHint")
                      : t("confirm.passwordChangedHint")}
                  </AlertDescription>
                </Alert>
              )}
              {verifyNotice}
              <OAuthButtons />
              <Form {...loginForm}>
                <form
                  data-testid="login-form"
                  className="space-y-4"
                  onSubmit={loginForm.handleSubmit(submitLogin)}
                >
                  <FormField
                    control={loginForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("login.email")}</FormLabel>
                        <FormControl>
                          <Input type="email" autoComplete="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={loginForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("login.password")}</FormLabel>
                        <FormControl>
                          <Input type="password" autoComplete="current-password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {t("login.submit")}
                  </Button>
                  <div className="text-center">
                    <ForgotPasswordDialog />
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="register">
          <Card>
            <CardHeader>
              <CardTitle>{t("login.createTitle")}</CardTitle>
              <CardDescription>{t("login.createSubtitle")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {verifyNotice}
              <OAuthButtons signUp />
              <Form {...registerForm}>
                <form
                  data-testid="register-form"
                  className="space-y-4"
                  // The sign-up proof of work solves while the visitor types (lib/auth/pow.ts).
                  onFocus={prefetchPowSolution}
                  onSubmit={registerForm.handleSubmit(submitRegister)}
                >
                  <FormField
                    control={registerForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("login.name")}</FormLabel>
                        <FormControl>
                          <Input autoComplete="name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={registerForm.control}
                    name="locale"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("language.label")}</FormLabel>
                        <FormControl>
                          <LanguageSelect
                            value={field.value}
                            onChange={(locale) => {
                              field.onChange(locale);
                              // Show the rest of the form in the chosen language right away.
                              void i18n.changeLanguage(locale);
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={registerForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("login.email")}</FormLabel>
                        <FormControl>
                          <Input type="email" autoComplete="email" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={registerForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("login.password")}</FormLabel>
                        <FormControl>
                          <Input type="password" autoComplete="new-password" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={registerForm.control}
                    name="acceptTerms"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-start gap-2">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={(checked) => field.onChange(checked === true)}
                              className="mt-0.5"
                              data-testid="accept-terms"
                            />
                          </FormControl>
                          <FormLabel className="text-sm leading-snug font-normal">
                            <Trans
                              i18nKey="login.acceptTerms"
                              components={{
                                terms: <LegalLink href={LEGAL_URLS.terms} />,
                                privacy: <LegalLink href={LEGAL_URLS.privacy} />,
                              }}
                            />
                          </FormLabel>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {t("login.create")}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      <nav
        className="mt-6 flex justify-center gap-4 text-xs text-muted-foreground"
        aria-label={t("legal.label")}
      >
        <a href={LEGAL_URLS.imprint} className="hover:underline">
          {t("legal.imprint")}
        </a>
        <a href={LEGAL_URLS.privacy} className="hover:underline">
          {t("legal.privacy")}
        </a>
        <a href={LEGAL_URLS.terms} className="hover:underline">
          {t("legal.terms")}
        </a>
      </nav>
    </div>
  );
}

/** A link inside the terms sentence; opens the website in a new tab (the form stays filled). */
function LegalLink({ href, children }: { href: string; children?: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="underline underline-offset-4">
      {children}
    </a>
  );
}
